import { ContactsOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import { useGetUserQuery } from "api/TenantSlug/User/hooks.ts";
import { ManageAdvertiserForm } from "components/Forms/ManageAdvertiserForm/ManageAdvertiserForm.tsx";
import { AdvertisersTable } from "components/Tables/AdvertisersTable/AdvertisersTable.tsx";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent.tsx";
import { TenantPage } from "components/UI/TenantPage/TenantPage.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions.ts";
import { useUser } from "hooks/useUser.ts";
import { PermissionEnum } from "types/enums";
import { FormId } from "types/models.ts";
import { createTenantFileRoute } from "utils/createTenantFileRoute";
import { getAdvertiserHeaderItems } from "utils/tenantHeaderItems.ts";

export const Route = createTenantFileRoute("/_authenticated/$tenantSlug/_layout/advertiser/_layout/")({
    component: AdvertiserPage,
    staticData: {
        permissions: [PermissionEnum.AdvertiserManage],
        showInSideMenu: true,
        label: "pages.advertiser.title",
        icon: <ContactsOutlined />,
        sort: 5,
    },
    loader: ({ context: { t } }) => ({
        breadcrumb: t("pages.advertiser.title"),
    }),
});

function AdvertiserPage() {
    const { t: tC } = usePaldockTranslation("common");
    const permissionUtils = usePermission();
    const userUtils = useUser();
    const canViewForeign = permissionUtils.hasPermission(PermissionEnum.RoleViewForeign);
    const authenticatedUser = userUtils.getAuthenticatedUser();
    const selfUserQuery = useGetUserQuery(
        authenticatedUser?.id ?? "",
        { filter: { include: "tenantSettings" } },
        {
            enabled: !canViewForeign && !!authenticatedUser?.id,
            refetchOnMount: "always",
            staleTime: 0,
        }
    );

    if (!canViewForeign) {
        if (!authenticatedUser?.id) {
            return <>{tC("errors.no_tenant_id")}</>;
        }

        if (selfUserQuery.isLoading || !selfUserQuery.data?.user) {
            return (
                <TenantPage
                    headerNavigationItems={getAdvertiserHeaderItems}
                    isEdit={true}
                    saveFormId={FormId.AddUserForm}
                >
                    <div className="flex justify-center items-center h-64">
                        <Spin size="large" />
                    </div>
                </TenantPage>
            );
        }

        return (
            <TenantPage headerNavigationItems={getAdvertiserHeaderItems} isEdit={true} saveFormId={FormId.AddUserForm}>
                <div className="w-full max-w-[1000px] mx-auto">
                    <DrawerContent>
                        <ManageAdvertiserForm mode="detail" user={selfUserQuery.data.user} />
                    </DrawerContent>
                </div>
            </TenantPage>
        );
    }

    return (
        <TenantPage headerNavigationItems={getAdvertiserHeaderItems}>
            <AdvertisersTable />
        </TenantPage>
    );
}
